// eslint-disable-next-line max-classes-per-file
import { injectable } from 'inversify';
import { Transform } from 'class-transformer';

import { Calendar } from '../calendar';

import { Filter, FilterType } from './filter.entity';
import { IsFilters } from './is-filters.validator';
import { filtersTransform } from './filters.transform';

/**
 * Часть, которую {@link FilterService.buildDto} добавляет к расширяемому классу
 */
interface FiltersClass {
  filters: {
    [key: string]: string | string[] | boolean;
  };
}

@injectable()
export class FilterService {
  constructor(private calendar: Calendar) {}

  /**
   * Возвращает новый класс с фильтрами
   * Расширяющая часть описана типом {@link FiltersClass}
   *
   * @typeParam T - Класс, который будет расширен
   * @param Dto - Расширяемый класс
   * @param filters - Фильтры
   * @returns Новый класс с фильтрами
   */
  buildDto<T extends new (...args: any[]) => object>(
    Dto: T,
    filters: Filter[],
  ) {
    class FiltersDto extends Dto implements FiltersClass {
      @Transform(filtersTransform(filters))
      @IsFilters(filters)
      filters: FiltersClass['filters'];
    }

    return FiltersDto;
  }

  /**
   * Преобразует даты начала и окончания для Dateable значения без из строки в дату
   * Если значение имеет тип отличный от Dateable, то никаких изменений не вносится
   *
   * @param value - Преобразуемое значение
   */
  sanitizeStringToDate(value: FilterType[string]) {
    if (
      typeof value !== 'object' ||
      Array.isArray(value) ||
      !(value.start ?? value.end)
    ) {
      return;
    }

    if (value.start) {
      value.start = new Date(value.start);
    }
    if (value.end) {
      value.end = new Date(value.end);
    }
  }

  /** Преобразует строковое значение boolean из строки в boolean
   * Если значение имеет тип отличный от строки 'true' | 'false', то никаких изменений не вносится
   * @param value - Преобразуемое значение
   * @returns Преобразованное значение
   */
  sanitizeBooleanStringToBoolean(value: FilterType[string]) {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    return value;
  }
}
